<template>
  <div
    v-if="show"
    class="modal fade show session-timeout-modal"
    tabindex="-1"
    style="display: block; background-color: rgba(0, 0, 0, 0.5)"
    @click.self="handleBackdropClick"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow-lg">
        <div class="modal-header border-0 pb-0">
          <div class="d-flex align-items-center">
            <div class="me-3">
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="12" cy="12" r="10" fill="#fbbf24" />
                <path
                  d="M12 6v6m0 4h.01"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <div>
              <h5 class="modal-title mb-0 fw-bold text-dark">Session Warning</h5>
            </div>
          </div>
        </div>
        
        <div class="modal-body pt-2 text-white">
          <p class="text-muted mb-4">
            {{ message || 'Your session will expire soon due to inactivity.' }}
          </p>
          
          <div class="alert alert-warning d-flex align-items-center mb-4 text-white" role="alert">
            <svg
              class="me-2"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="12" cy="12" r="10" stroke="#f59e0b" stroke-width="2" />
              <path
                d="M12 6v6m0 4h.01"
                stroke="#f59e0b"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
                         <div>
               <strong>Auto logout in {{ remainingTime }} minute(s)</strong>
               <br />
               <small>Click "Stay Logged In" to extend your session</small>
             </div>
          </div>

          <div class="row g-3">
            <div class="col-md-6">
              <button
                type="button"
                class="btn btn-outline-secondary w-100"
                @click="handleLogout"
                :disabled="isLoading"
              >
                <i class="fas fa-sign-out-alt me-2"></i>
                Logout Now
              </button>
            </div>
            <div class="col-md-6">
              <button
                type="button"
                class="btn btn-success w-100"
                @click="handleExtendSession"
                :disabled="isLoading"
              >
                <i class="fas fa-clock me-2"></i>
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

export default {
  name: 'SessionTimeoutModal',
  setup() {
    const store = useStore();
    const router = useRouter();
    const isLoading = ref(false);
    const countdownInterval = ref(null);
    const remainingTime = ref(2);

    // Computed properties
    const sessionStatus = computed(() => store.getters['auth/sessionStatus']);
    const show = computed(() => sessionStatus.value.showWarning);
    const message = computed(() => sessionStatus.value.warningMessage);

    // Methods
    const handleExtendSession = async () => {
      try {
        isLoading.value = true;
        
        // Stop countdown
        stopCountdown();
        
        await store.dispatch('auth/extendSession');
        console.log('Session extended successfully');
        
      } catch (error) {
        console.error('Error extending session:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const handleLogout = async () => {
      try {
        isLoading.value = true;
        
        // Stop countdown first
        stopCountdown();
        
        const result = await store.dispatch('auth/logout');
        
        if (result.success) {
          router.push({ name: 'Signin' });
        }
      } catch (error) {
        console.error('Error during logout:', error);
        // Even if error, still redirect to login
        router.push({ name: 'Signin' });
      } finally {
        isLoading.value = false;
      }
    };

    const handleBackdropClick = () => {
      // Prevent closing modal by clicking backdrop during session warning
      // User must make a choice
      return;
    };

    const startCountdown = () => {
      remainingTime.value = 2; // 2 minutes warning
      
      countdownInterval.value = setInterval(() => {
        remainingTime.value -= 1;
        
        if (remainingTime.value <= 0) {
          clearInterval(countdownInterval.value);
          // Auto logout when countdown reaches 0
          console.log('Auto logout triggered by countdown reaching 0');
          handleLogout();
        }
      }, 60000); // Update every minute
    };

    const stopCountdown = () => {
      if (countdownInterval.value) {
        clearInterval(countdownInterval.value);
        countdownInterval.value = null;
      }
    };

    // Lifecycle hooks
    onMounted(() => {
      // Watch for changes in modal visibility
      store.watch(
        () => store.getters['auth/sessionStatus'].showWarning,
        (newValue) => {
          if (newValue) {
            startCountdown();
          } else {
            stopCountdown();
          }
        }
      );
    });

    onUnmounted(() => {
      stopCountdown();
    });

    return {
      show,
      message,
      remainingTime,
      isLoading,
      handleExtendSession,
      handleLogout,
      handleBackdropClick
    };
  }
};
</script>

<style scoped>
.session-timeout-modal {
  z-index: 9999;
}

.modal-content {
  border-radius: 12px;
  background: white;
}

.modal-header {
  padding: 1.5rem 1.5rem 0.5rem 1.5rem;
}

.modal-body {
  padding: 0.5rem 1.5rem 1.5rem 1.5rem;
}

.btn {
  border-radius: 8px;
  font-weight: 500;
  padding: 0.75rem 1rem;
  transition: all 0.2s ease;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.btn:disabled {
  transform: none;
  box-shadow: none;
}

.alert {
  border-radius: 8px;
  border: 1px solid #fbbf24;
  background-color: #fef3c7;
}

.btn-success {
  background: linear-gradient(45deg, #22c55e, #16a34a);
  border: none;
  color: white;
}

.btn-success:hover {
  background: linear-gradient(45deg, #16a34a, #15803d);
}

.btn-outline-secondary {
  border-color: #d1d5db;
  color: #6b7280;
}

.btn-outline-secondary:hover {
  background-color: #f3f4f6;
  border-color: #9ca3af;
}

@media (max-width: 576px) {
  .modal-dialog {
    margin: 1rem;
  }
  
  .row.g-3 .col-md-6 {
    margin-bottom: 0.5rem;
  }
}
</style> 